import { useState, type FC, useEffect, type Dispatch, type SetStateAction, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type PreparePaymentRequest, preparePayment, type EwayAccessCode } from '@/api/registration/payment';
import { type ApiResponse } from '@/api/tenantClient';
import { useTenantApi } from '@/account/hook/useTenantApi';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import PayByEWay from './PayByEWay';
import { useGroupCartQuery } from '@/registration/hook/useGroupCartQuery';
import { SkeletonText } from '@chakra-ui/react';
import { cartQueryKey } from '@/api/registration';

export interface PayByEwayWrapperProps {
  setErrors: Dispatch<SetStateAction<string[]>>
  isValid: boolean
}

const PayByEwayWrapper: FC<PayByEwayWrapperProps> = ({
  setErrors,
  isValid,
  ...props
}) => {
  const [options, setOptions] = useState<EwayAccessCode>();
  const { createTenantApiRequest } = useTenantApi();
  const { eventId } = useRegisterRoute();
  const queryClient = useQueryClient();
  const { data: cart } = useGroupCartQuery(eventId);

  const { mutate: preparePaymentMutate, isLoading } = useMutation<ApiResponse<EwayAccessCode>, Error, PreparePaymentRequest>({
    mutationFn: async (preparePaymentData: PreparePaymentRequest) => {
      return await preparePayment(createTenantApiRequest)(preparePaymentData) as ApiResponse<EwayAccessCode>;
    },
    onSuccess: (result) => {
      if (result.errors && result.errors.length > 0) {
        setErrors(result.errors);
      } else {
        setOptions(result.items[0]);
        void queryClient.invalidateQueries({ queryKey: [cartQueryKey, { registrationId: cart?.registrationId }] });
      }
    },
    onError: (error: any) => {
      setErrors([error.message ?? 'Failed to save the host.']);
    }
  });

  useEffect(() => {
    if (cart) {
      const preparePaymentData: PreparePaymentRequest = {
        cartId: cart.id,
        locale: 'aud',
        gateway: 'eWay'
      };
      setErrors([]);
      preparePaymentMutate(preparePaymentData);
    }
  }, [cart]);

  return isLoading ? (
    <SkeletonText mt='4' noOfLines={4} spacing='4' skeletonHeight='8' />
  ) : options?.accessCodesShared && cart && (
    <PayByEWay
      setErrors={setErrors}
      isValid={isValid}
      options={options}
      cart={cart}
    />
  );
};

export default PayByEwayWrapper;
